import React from 'react';
import dayjs from 'dayjs';
import { Button } from '@bedrock/components';
import createFormItemRow, {
  type FormItemProp,
} from '#components/SForm/createFormItemRow';
import TextAreaWithUpload from '#components/SForm/SFormElement/TextAreaWithUpload';
import CopyIcon from '#components/CopyIcon';
import RechargeBooleanTag from './Drawer/RechargeBooleanTag';
import RechargeDemandStateTag from './Drawer/RechargeDemandStateTag';
import { $sType } from '#utils/index';
import { openDetailPath } from '#utils/openDetail';
import { Z_INDEX } from '#utils/zIndex';
import type { RechargeRenderData } from './types';
import {
  FIELD_KEYS,
  AMOUNT_MIN,
  AMOUNT_DECIMAL_PLACES,
  ATTACHMENT_MAX_SIZE,
} from './constants';
import {
  isFieldVisible,
  isFieldRequired,
  toStringOptions,
  reasonFieldValidator,
} from './formConfig';

/** 字段显隐辅助（收窄 renderData 类型，避免 any） */
const vis = (field: string) => (rd: RechargeRenderData) =>
  isFieldVisible(field, rd);
const req = (field: string) => (rd: RechargeRenderData) =>
  isFieldRequired(field, rd);

/**
 * currencyType 字段的 formItemKey 用虚拟 key（$ 前缀，对齐退款 FormModel $refund_amount）：
 * SFormItem 的 Form.Item name = formItemKey，若与 formItemKeyMap.value 目标字段（真实字段名）
 * 重名会导致一个字段被 Form.Item 与 FormItemValueTransform 双重写入冲突。
 * formItemKeyMap.value 指向真实字段（order_amount/budget_amount/quote_price），
 * submit 时从 form 真实字段取值。
 */
export const VIRTUAL_KEY_ORDER_AMOUNT = '$order_amount';
export const VIRTUAL_KEY_BUDGET_AMOUNT = '$budget_amount';
export const VIRTUAL_KEY_QUOTE_PRICE = '$quote_price';

/** 执行日期区间格式化（draft 返回 ISO string，展示 YYYY-MM-DD；空值灰色 --） */
const formatExecuteDate = (raw?: unknown): string => {
  if (!raw || typeof raw !== 'string') return '';
  const d = dayjs(raw);
  return d.isValid() ? d.format('YYYY-MM-DD') : raw;
};

/** 执行日期区间展示：start ~ end（end 缺省只显示开始；均缺省 --） */
const formatExecuteDateRange = (
  start?: unknown,
  end?: unknown
): string => {
  const s = formatExecuteDate(start);
  const e = formatExecuteDate(end);
  if (s && e) return `${s} ~ ${e}`;
  return s || '';
};

/**
 * 充值发起表单字段配置（纯数据，单一字段源）。
 * 同时被：
 * - 单条发起 FormModel createFormItemRow(rechargeFormModelFields)（SForm 消费，renderType 由调用方控制）
 * - 批量表格 formModelToColumns(rechargeFormModelFields)（ProTableForm formColumns 消费）
 * 复用，达到模型和交互逻辑对齐（对齐退款 refundFormModelFields）。
 *
 * 字段显隐/必填由 formConfig 按 角色×入口×海外×核算预判 动态控制（show/required 函数式）。
 * 金额类字段复用 CurrencyType（千分位 + 币种只读，对齐退款 FormModel $refund_amount）：
 *  - order_amount：可编辑，币种不可改（selectDisabled）
 *  - budget_amount / quote_price：只读展示（view: true）
 * 执行日期用 input + showComponent 格式化展示（SFormElement datepicker view 直接拼 value，
 * dayjs 对象会序列化出错，故不自带 dayjs 转换）。
 * value/onChange 由 SFormItem + Form 管理；TextAreaWithUpload 通过 render 渲染。
 */
/** 只读文本字段的 showComponent：空值显示 --（对齐 EXECUTE_DATE 的 showComponent 范式） */
const readOnlyText = (value: unknown) => () => (
  <span>{value !== undefined && value !== null && value !== '' ? String(value) : <span className="text-color-3">--</span>}</span>
);

/** 二/三级单号展示：内包跳转需求详情（CHANNEL_GROUP）+ 复制，外包仅复制（无需求详情路由），对齐退款 FormModel l2/l3 */
const renderDemandIdLink = (value: unknown, state?: unknown, stateName?: unknown) => () => {
  const text = value !== undefined && value !== null && value !== '' ? String(value) : '';
  const stateText = stateName != null && stateName !== '' ? String(stateName) : '';
  const idRow = text ? (
    $sType === 'outer' ? (
      <div className="flex items-center">
        <span>{text}</span>
        <CopyIcon text={text} tipToolTitle="复制" />
      </div>
    ) : (
      <div className="flex items-center">
        <Button type="link" onClick={() => openDetailPath('CHANNEL_GROUP', text)}>
          {text}
        </Button>
        <CopyIcon text={text} tipToolTitle="复制" />
      </div>
    )
  ) : (
    <span className="text-color-3">--</span>
  );
  return (
    <div className="flex flex-col gap-1">
      {idRow}
      {stateText ? (
        <RechargeDemandStateTag state={state as string | null | undefined} stateName={stateText} />
      ) : null}
    </div>
  );
};

export const rechargeFormModelFields: Record<string, FormItemProp> = {
  // 只读单据上下文字段（发起态：二级单号/三级单号/供应商名称/充值单号；充值单号发起态为空显示 --）
  [FIELD_KEYS.L2_DEMAND_ID]: {
    label: '二级单号',
    col: 12,
    formItemKey: FIELD_KEYS.L2_DEMAND_ID,
    componentClass: 'input',
    view: true,
    show: true,
    elementProps: ({ detailData }: { detailData: RechargeRenderData['detailData'] }) => ({
      showComponent: renderDemandIdLink(
        detailData?.l2_demand_id,
        detailData?.l2_demand_state,
        detailData?.l2_demand_state_name
      ),
    }),
  },
  [FIELD_KEYS.L3_DEMAND_ID]: {
    label: '三级单号',
    col: 12,
    formItemKey: FIELD_KEYS.L3_DEMAND_ID,
    componentClass: 'input',
    view: true,
    show: true,
    elementProps: ({ detailData }: { detailData: RechargeRenderData['detailData'] }) => ({
      showComponent: renderDemandIdLink(
        detailData?.l3_demand_id,
        detailData?.l3_demand_state,
        detailData?.l3_demand_state_name
      ),
    }),
  },
  [FIELD_KEYS.SUPPLIER_NAME]: {
    label: '供应商名称',
    col: 12,
    formItemKey: FIELD_KEYS.SUPPLIER_NAME,
    componentClass: 'input',
    view: true,
    show: true,
    elementProps: ({ detailData }: { detailData: RechargeRenderData['detailData'] }) => ({
      showComponent: readOnlyText(detailData?.supplier_name),
    }),
  },
  [FIELD_KEYS.ORDER_NO]: {
    label: '充值单号',
    col: 12,
    formItemKey: FIELD_KEYS.ORDER_NO,
    componentClass: 'input',
    view: true,
    show: true,
    elementProps: ({ detailData }: { detailData: RechargeRenderData['detailData'] }) => ({
      showComponent: () => {
        const text = detailData?.order_no;
        return text ? (
          <div className="flex items-center">
            <span>{text}</span>
            <CopyIcon text={String(text)} tipToolTitle="复制" />
          </div>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
  },
  [VIRTUAL_KEY_ORDER_AMOUNT]: {
    label: '充值金额',
    col: 12,
    formItemKey: VIRTUAL_KEY_ORDER_AMOUNT,
    componentClass: 'currencyType',
    // Form.Item value 为 {value, currency} 对象，不能用 type:'number' rules（会误报）；
    // 金额有效性由 CurrencyType 内部 InputNumber（min=0.01 + 千分位）保证，required 走对象非空校验
    elementProps: {
      selectDisabled: true,
      min: AMOUNT_MIN,
      precision: AMOUNT_DECIMAL_PLACES,
    },
    formItemKeyMap: {
      value: FIELD_KEYS.ORDER_AMOUNT,
      currency: 'currency',
    },
    show: vis(FIELD_KEYS.ORDER_AMOUNT),
    required: req(FIELD_KEYS.ORDER_AMOUNT),
  },
  [FIELD_KEYS.ACCOUNT_ID_NAME]: {
    label: '充值账号ID/账号名',
    col: 12,
    formItemKey: FIELD_KEYS.ACCOUNT_ID_NAME,
    componentClass: 'input',
    elementProps: { maxLength: 200, showCount: true },
    show: vis(FIELD_KEYS.ACCOUNT_ID_NAME),
    required: req(FIELD_KEYS.ACCOUNT_ID_NAME),
  },
  [FIELD_KEYS.CHANNEL]: {
    label: '投放渠道',
    col: 12,
    formItemKey: FIELD_KEYS.CHANNEL,
    componentClass: 'select',
    elementProps: (rd: RechargeRenderData) => ({
      options: toStringOptions(rd?.detailData?.channel_options),
      placeholder: '请选择投放渠道',
      allowClear: true,
      // 下拉弹层 zIndex 高于抽屉（对齐退款 Z_INDEX.drawerPopup），避免被抽屉盖住看不到选项
      dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
      // view 模式（审批详情）：渠道文本由 detail.channel 直接展示，不依赖 channel_options 反查
      // （审批详情 VO 无 channel_options，select 默认 view 会 findOption 空 → '--'）
      showComponent: () => {
        const text = rd?.detailData?.channel;
        return text ? (
          <span>{text}</span>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
    show: vis(FIELD_KEYS.CHANNEL),
    required: req(FIELD_KEYS.CHANNEL),
  },
  [FIELD_KEYS.PLATFORM]: {
    label: '合作平台',
    col: 12,
    formItemKey: FIELD_KEYS.PLATFORM,
    componentClass: 'select',
    elementProps: (rd: RechargeRenderData) => ({
      options: toStringOptions(rd?.detailData?.platform_options),
      placeholder: '请选择合作平台',
      allowClear: true,
      // 下拉弹层 zIndex 高于抽屉（对齐退款 Z_INDEX.drawerPopup），避免被抽屉盖住看不到选项
      dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
      // view 模式（审批详情）：平台文本由 detail.platform 直接展示，不依赖 platform_options 反查
      showComponent: () => {
        const text = rd?.detailData?.platform;
        return text ? (
          <span>{text}</span>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
    show: vis(FIELD_KEYS.PLATFORM),
    required: req(FIELD_KEYS.PLATFORM),
  },
  [VIRTUAL_KEY_BUDGET_AMOUNT]: {
    label: '预算金额',
    col: 12,
    formItemKey: VIRTUAL_KEY_BUDGET_AMOUNT,
    componentClass: 'currencyType',
    view: true,
    // 只读展示：复用 CurrencyType view 模式（金额千分位 + 币种 label），不手动拼接（对齐退款 $budget_amount）
    elementProps: { selectDisabled: true },
    formItemKeyMap: {
      value: FIELD_KEYS.BUDGET_AMOUNT,
      currency: FIELD_KEYS.BUDGET_CURRENCY,
    },
    show: vis(FIELD_KEYS.BUDGET_AMOUNT),
  },
  [VIRTUAL_KEY_QUOTE_PRICE]: {
    label: '定标金额',
    col: 12,
    formItemKey: VIRTUAL_KEY_QUOTE_PRICE,
    componentClass: 'currencyType',
    view: true,
    elementProps: { selectDisabled: true },
    formItemKeyMap: {
      value: FIELD_KEYS.QUOTE_PRICE,
      currency: FIELD_KEYS.QUOTE_CURRENCY,
    },
    show: vis(FIELD_KEYS.QUOTE_PRICE),
  },
  [FIELD_KEYS.EXECUTE_DATE]: {
    label: '执行日期',
    col: 12,
    formItemKey: FIELD_KEYS.EXECUTE_DATE,
    componentClass: 'input',
    view: true,
    show: vis(FIELD_KEYS.EXECUTE_DATE),
    elementProps: ({
      detailData,
    }: {
      detailData: RechargeRenderData['detailData'];
    }) => ({
      showComponent: () => {
        const text = formatExecuteDateRange(
          detailData?.execute_date_start,
          detailData?.execute_date_end
        );
        return text ? (
          <span>{text}</span>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
  },
  [FIELD_KEYS.IS_EARLY_RECHARGE]: {
    label: '是否提前充值',
    col: 12,
    formItemKey: FIELD_KEYS.IS_EARLY_RECHARGE,
    componentClass: 'input',
    view: true,
    elementProps: () => ({
      showComponent: (value: unknown) => (
        <RechargeBooleanTag value={value as boolean | null | undefined} />
      ),
    }),
    show: vis(FIELD_KEYS.IS_EARLY_RECHARGE),
  },
  [FIELD_KEYS.EARLY_RECHARGE_REASON]: {
    label: '提前充值原因',
    col: 24,
    formItemKey: FIELD_KEYS.EARLY_RECHARGE_REASON,
    validateTrigger: false,
    show: vis(FIELD_KEYS.EARLY_RECHARGE_REASON),
    required: req(FIELD_KEYS.EARLY_RECHARGE_REASON),
    rules: (rd: RechargeRenderData) =>
      isFieldRequired(FIELD_KEYS.EARLY_RECHARGE_REASON, rd)
        ? [reasonFieldValidator('请填写提前充值原因或上传附件')]
        : [],
    render: () => (
      <TextAreaWithUpload
        itemKey={FIELD_KEYS.EARLY_RECHARGE_REASON}
        maxFileSize={ATTACHMENT_MAX_SIZE}
        // 抽屉为固定层级（Z_INDEX.drawer），浮层需显式抬高，否则被抽屉自身盖住
        zIndex={Z_INDEX.drawerPopup}
        textAreaProps={{
          maxLength: 500,
          showCount: true,
          autoSize: { minRows: 2, maxRows: 6 },
        }}
      />
    ),
  },
  [FIELD_KEYS.IS_OVER_CHARGE]: {
    label: '是否超充',
    col: 12,
    formItemKey: FIELD_KEYS.IS_OVER_CHARGE,
    componentClass: 'input',
    view: true,
    elementProps: () => ({
      showComponent: (value: unknown) => (
        <RechargeBooleanTag value={value as boolean | null | undefined} />
      ),
    }),
    show: vis(FIELD_KEYS.IS_OVER_CHARGE),
  },
  [FIELD_KEYS.OVER_CHARGE_REASON]: {
    label: '超充原因',
    col: 24,
    formItemKey: FIELD_KEYS.OVER_CHARGE_REASON,
    validateTrigger: false,
    show: vis(FIELD_KEYS.OVER_CHARGE_REASON),
    required: req(FIELD_KEYS.OVER_CHARGE_REASON),
    rules: (rd: RechargeRenderData) =>
      isFieldRequired(FIELD_KEYS.OVER_CHARGE_REASON, rd)
        ? [reasonFieldValidator('请填写超充原因或上传附件')]
        : [],
    render: () => (
      <TextAreaWithUpload
        itemKey={FIELD_KEYS.OVER_CHARGE_REASON}
        maxFileSize={ATTACHMENT_MAX_SIZE}
        // 抽屉为固定层级（Z_INDEX.drawer），浮层需显式抬高，否则被抽屉自身盖住
        zIndex={Z_INDEX.drawerPopup}
        textAreaProps={{
          maxLength: 500,
          showCount: true,
          autoSize: { minRows: 2, maxRows: 6 },
        }}
      />
    ),
  },
  [FIELD_KEYS.ATTACHMENT]: {
    label: '充值附件',
    col: 24,
    formItemKey: FIELD_KEYS.ATTACHMENT,
    validateTrigger: false,
    required: true,
    rules: [reasonFieldValidator('请填写充值说明或上传充值附件')],
    // 供应商确认节点：附件并入详情网格可编辑（对齐退款 attachments 字段，Design Feedback 2026-08-25）
    // 仅由 RechargeConfirmView 的 confirmItemCols 按 isSupplierConfirm 显式引入，发起态不渲染
    render: () => (
      <TextAreaWithUpload
        itemKey={FIELD_KEYS.ATTACHMENT}
        maxFileSize={ATTACHMENT_MAX_SIZE}
        // 供应商确认附件用 block 布局（textarea + SUpload 上下直列），对齐退款 attachments 字段（Design Feedback 2026-08-25）
        displayType="block"
        textAreaProps={{
          maxLength: 500,
          showCount: true,
          autoSize: { minRows: 2, maxRows: 6 },
        }}
      />
    ),
  },
  [FIELD_KEYS.OPTIMIZER_IDS]: {
    label: '优化师',
    col: 24,
    formItemKey: FIELD_KEYS.OPTIMIZER_IDS,
    componentClass: 'select',
    elementProps: (rd: RechargeRenderData) => ({
      multiple: true,
      showSearch: true,
      placeholder: '请选择优化师',
      options: rd?.optionMap?.optimizer || [],
      allowClear: true,
      // 下拉弹层 zIndex 高于抽屉（对齐退款 Z_INDEX.drawerPopup），避免被抽屉盖住看不到选项
      dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
      // view 模式（审批详情）：优化师名单由字段 value（对象数组 { user_id, name }，buildRechargeConfirmValues
      // 映射）直接展示，对齐退款 optimizers 字段 showComponent 读 value 的逻辑
      showComponent: (value: unknown) => {
        const list = ((value ?? []) as Array<{ name?: string }>).filter(
          (o) => o?.name
        );
        return list.length ? (
          <span>{list.map((o) => o.name).join('、')}</span>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
    show: vis(FIELD_KEYS.OPTIMIZER_IDS),
    required: req(FIELD_KEYS.OPTIMIZER_IDS),
  },
};

/** 全字段顺序（PRD §4.1.1.4.1 单个充值字段表）；隐藏字段由 show 控制不渲染 */
export const RECHARGE_FIELD_COLS = [
  FIELD_KEYS.L2_DEMAND_ID,
  FIELD_KEYS.L3_DEMAND_ID,
  FIELD_KEYS.SUPPLIER_NAME,
  FIELD_KEYS.ORDER_NO,
  VIRTUAL_KEY_ORDER_AMOUNT,
  FIELD_KEYS.ACCOUNT_ID_NAME,
  FIELD_KEYS.CHANNEL,
  FIELD_KEYS.PLATFORM,
  VIRTUAL_KEY_BUDGET_AMOUNT,
  VIRTUAL_KEY_QUOTE_PRICE,
  FIELD_KEYS.EXECUTE_DATE,
  FIELD_KEYS.IS_EARLY_RECHARGE,
  FIELD_KEYS.EARLY_RECHARGE_REASON,
  FIELD_KEYS.IS_OVER_CHARGE,
  FIELD_KEYS.OVER_CHARGE_REASON,
  FIELD_KEYS.OPTIMIZER_IDS,
] as const;

const RechargeFormModel = createFormItemRow(rechargeFormModelFields);

export default RechargeFormModel;
